import { useEffect, useRef } from 'react'
import {
  TimerReset,
  Rocket,
  ShieldCheck,
  Radar,
  type LucideIcon,
} from 'lucide-react'
import SectionHeading from './SectionHeading'
import { IMPACT_METRICS } from '../data/impact'
import { initGSAP, prefersReducedMotion } from '../lib/motion'

const ICONS: LucideIcon[] = [TimerReset, Rocket, ShieldCheck, Radar]

const Impact: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-impact]',
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          stagger: 0.14,
          scrollTrigger: {
            trigger: ref.current,
            start: 'top 80%',
            once: true,
          },
        }
      )

      gsapApi.utils
        .toArray<HTMLElement>('[data-impact-metric]')
        .forEach((el) => {
          const match = (el.dataset.metric || '').match(/^(\d+)(.+)?$/)
          if (!match) return
          const target = { value: 0 }
          gsapApi.fromTo(
            target,
            { value: 0 },
            {
              value: Number(match[1]),
              duration: 1.4,
              ease: 'power2.out',
              scrollTrigger: { trigger: el, start: 'top 88%', once: true },
              onUpdate: () => {
                el.textContent = `${Math.round(target.value)}${match[2] ?? ''}`
              },
            }
          )
        })
    }, ref)
    return () => ctx.revert()
  }, [])

  return (
    <section id="impact" ref={ref} className="section-padding relative">
      <div className="container-width">
        <SectionHeading
          label="Impact"
          meta="metrics"
          title={
            <>
              Production-grade{' '}
              <span className="text-gradient">platform outcomes</span>
            </>
          }
          description="Measurable results from two years spent building cloud platforms, Kubernetes infrastructure, and developer experience tooling at Codincloud."
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {IMPACT_METRICS.map((item, i) => {
            const Icon = ICONS[i]
            return (
              <div
                key={item.value}
                data-impact
                className="group relative rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-7 transition-all duration-200 hover:shadow-lg hover:shadow-primary-500/[0.06]"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-500/10 text-primary-500">
                    <Icon size={20} />
                  </span>
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                    impact
                  </div>
                </div>

                <div
                  data-impact-metric
                  data-metric={item.value}
                  className="font-display text-[2.5rem] font-semibold leading-none tracking-tight text-neutral-900 dark:text-neutral-50"
                >
                  {item.value}
                </div>

                <p className="mt-3 text-sm font-medium text-neutral-900 dark:text-neutral-100 leading-snug">
                  {item.label}
                </p>

                <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  {item.detail}
                </p>

                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary-500/[0.04] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default Impact